import type { AiCustomPart } from 'juneau';
import type { ApprovalStatus, CompletionCondition } from '@insio/shared-types';

/**
 * What the user came for, which decides what the card puts first. Mirrors
 * InvoiceCardVariant in @insio/plugin-catalog — the emitting tool picks it.
 */
export type InvoiceCardVariant = 'lookup' | 'validation' | 'payment' | 'approval';

const VARIANTS: readonly string[] = ['lookup', 'validation', 'payment', 'approval'];

/** Falls back to `lookup` — an unknown variant must still render a usable card. */
export function toInvoiceCardVariant(value: unknown): InvoiceCardVariant {
  return typeof value === 'string' && VARIANTS.includes(value)
    ? (value as InvoiceCardVariant)
    : 'lookup';
}

/** Fields shared by a single invoice card and one row of a collection. */
export interface InvoiceRowData {
  id: string;
  invoiceNumber: string | null;
  supplier: string | null;
  date: string | null;
  dueDate?: string | null;
  amount: string | null;
  currency: string | null;
  /** Absent on cards stored before the approval status was carried; such a card shows no badge. */
  approvalStatus?: ApprovalStatus | null;
  isCancelled?: boolean;
  isCompleted?: boolean;
  unmetCompletionConditions?: CompletionCondition[];
  paymentStatus?: string | null;
  validationStatus: string | null;
  errorCount?: number;
  warningCount?: number;
}

export interface InvoiceCardPartData extends AiCustomPart, InvoiceRowData {
  type: 'invoice-card';
  variant?: InvoiceCardVariant;
}

/** Per-currency subtotal of a collection — money is never summed across currencies. */
export interface InvoiceCollectionTotal {
  currency: string;
  total: string;
  count: number;
}

export interface InvoiceCollectionPartData extends AiCustomPart {
  type: 'invoice-collection';
  variant?: InvoiceCardVariant;
  /** Rows carried in this part — may be fewer than `total`. */
  shown: number;
  /** Everything matching the query, including rows beyond the fetch window. */
  total: number;
  /** Rows read from the database and covered by the aggregates. */
  read: number;
  totalsByCurrency: InvoiceCollectionTotal[];
  errorCount?: number;
  warningCount?: number;
  invoices: InvoiceRowData[];
}

/** Sanity bound for ids rendered into navigation targets. */
const MAX_ID_LENGTH = 100;

/** True when the id is a plausible navigation target (non-empty, bounded string). */
export function isValidInvoiceCardId(id: unknown): id is string {
  return typeof id === 'string' && id.length > 0 && id.length <= MAX_ID_LENGTH;
}

/** Runtime guard for untrusted message parts claiming to be invoice cards. */
export function isInvoiceCardPart(part: {
  type: string;
  [key: string]: unknown;
}): part is InvoiceCardPartData {
  return part.type === 'invoice-card' && isValidInvoiceCardId(part.id);
}

/** Runtime guard for untrusted message parts claiming to be invoice collections. */
export function isInvoiceCollectionPart(part: {
  type: string;
  [key: string]: unknown;
}): part is InvoiceCollectionPartData {
  return part.type === 'invoice-collection' && Array.isArray(part.invoices);
}
